/**
 * Utility functions for file downloads.
 */

/**
 * Extract filename from Content-Disposition header.
 * @param contentDisposition - The Content-Disposition header value
 * @param fallback - Fallback filename if extraction fails
 * @returns The extracted filename or fallback
 */
export function extractFilenameFromHeader(
  contentDisposition: string | null,
  fallback: string,
): string {
  if (!contentDisposition) return fallback;

  // Try to match filename="..." or filename=...
  const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
  return filenameMatch?.[1] || fallback;
}

/**
 * Trigger a file download from a Blob.
 * @param blob - The Blob to download
 * @param filename - The filename for the download
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = downloadUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(downloadUrl);
}

/**
 * Fetch a file from a signed URL and trigger a browser download.
 * Used for downloading documents from pre-signed cloud storage URLs.
 *
 * @param signedUrl - The pre-signed URL to fetch the file from
 * @param filename - The filename for the download
 */
export async function downloadFromSignedUrl(signedUrl: string, filename: string): Promise<void> {
  const { fetchSignedUrl } = await import("./externalFetch");
  const response = await fetchSignedUrl(signedUrl, "application/pdf,image/*");
  const blob = await response.blob();
  downloadBlob(blob, filename);
}
